import React from 'react';
import { Box, Divider, Link, Typography } from '@mui/material';

const renderInlineMarkdown = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const regex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*[^*]+\*\*|\*[^*]+\*|~~[^~]+~~|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('[')) {
      parts.push(
        <Link key={key++} href={match[3]} target="_blank" rel="noopener noreferrer" underline="hover">
          {match[2]}
        </Link>,
      );
    } else if (token.startsWith('**')) {
      parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('~~')) {
      parts.push(<del key={key++}>{token.slice(2, -2)}</del>);
    } else if (token.startsWith('`')) {
      parts.push(
        <Box
          key={key++}
          component="code"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.85em',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
          }}
        >
          {token.slice(1, -1)}
        </Box>,
      );
    } else if (token.startsWith('*')) {
      parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

type MarkdownBlock =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'blockquote'; lines: string[] }
  | { type: 'hr' }
  | { type: 'code'; language: string; content: string };

const HEADING_RE = /^(#{1,6})\s+(.+)$/;
const UL_RE = /^[-*+]\s+(.+)$/;
const OL_RE = /^\d+\.\s+(.+)$/;
const BQ_RE = /^>\s?(.*)$/;
const HR_RE = /^(-{3,}|\*{3,}|_{3,})$/;

const parseTextBlocks = (text: string): MarkdownBlock[] => {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let i = 0;

  const pushParagraph = (buf: string[]) => {
    const joined = buf.join(' ').trim();
    if (joined) blocks.push({ type: 'paragraph', text: joined });
    buf.length = 0;
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    const heading = trimmed.match(HEADING_RE);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2].trim() });
      i += 1;
      continue;
    }

    if (HR_RE.test(trimmed)) {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    const bq = trimmed.match(BQ_RE);
    if (bq) {
      const quoteLines: string[] = [];
      while (i < lines.length) {
        const q = lines[i].trim().match(BQ_RE);
        if (!q) break;
        quoteLines.push(q[1]);
        i += 1;
      }
      blocks.push({ type: 'blockquote', lines: quoteLines });
      continue;
    }

    const ul = trimmed.match(UL_RE);
    if (ul) {
      const items: string[] = [];
      while (i < lines.length) {
        const item = lines[i].trim().match(UL_RE);
        if (!item) break;
        items.push(item[1]);
        i += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    const ol = trimmed.match(OL_RE);
    if (ol) {
      const items: string[] = [];
      while (i < lines.length) {
        const item = lines[i].trim().match(OL_RE);
        if (!item) break;
        items.push(item[1]);
        i += 1;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    const paraBuf: string[] = [trimmed];
    i += 1;
    while (i < lines.length) {
      const next = lines[i].trim();
      if (
        !next ||
        HEADING_RE.test(next) ||
        HR_RE.test(next) ||
        BQ_RE.test(next) ||
        UL_RE.test(next) ||
        OL_RE.test(next)
      ) {
        break;
      }
      paraBuf.push(next);
      i += 1;
    }
    pushParagraph(paraBuf);
  }

  return blocks;
};

const parseMarkdown = (content: string): MarkdownBlock[] => {
  const blocks: MarkdownBlock[] = [];
  const segments = content.split(/```/);

  segments.forEach((segment, index) => {
    if (index % 2 === 1) {
      const newline = segment.indexOf('\n');
      const language = newline >= 0 ? segment.slice(0, newline).trim() : '';
      const code = newline >= 0 ? segment.slice(newline + 1) : segment;
      blocks.push({ type: 'code', language, content: code.replace(/\n$/, '') });
      return;
    }
    if (segment.trim()) {
      blocks.push(...parseTextBlocks(segment));
    }
  });

  return blocks;
};

const headingVariant = (level: number): 'h4' | 'h5' | 'h6' => {
  if (level <= 2) return 'h4';
  if (level <= 4) return 'h5';
  return 'h6';
};

interface MarkdownPreviewProps {
  content: string;
}

export const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({ content }) => {
  const blocks = parseMarkdown(content);

  if (!content.trim()) {
    return (
      <Typography variant="body2" color="text.secondary">
        (empty)
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
      }}
    >
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'heading':
            return (
              <Typography
                key={`h-${index}`}
                variant={headingVariant(block.level)}
                sx={{ fontWeight: 700, mt: index === 0 ? 0 : 2, mb: 1, display: 'block' }}
              >
                {renderInlineMarkdown(block.text)}
              </Typography>
            );
          case 'paragraph':
            return (
              <Typography key={`p-${index}`} variant="body2" sx={{ mb: 1.25, lineHeight: 1.65 }}>
                {renderInlineMarkdown(block.text)}
              </Typography>
            );
          case 'ul':
            return (
              <Box
                key={`ul-${index}`}
                component="ul"
                sx={{ m: 0, mb: 1.25, pl: 2.5, '& li': { mb: 0.5 } }}
              >
                {block.items.map((item, itemIdx) => (
                  <Box key={itemIdx} component="li">
                    <Typography variant="body2" component="span" sx={{ lineHeight: 1.65 }}>
                      {renderInlineMarkdown(item)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          case 'ol':
            return (
              <Box
                key={`ol-${index}`}
                component="ol"
                sx={{ m: 0, mb: 1.25, pl: 2.5, '& li': { mb: 0.5 } }}
              >
                {block.items.map((item, itemIdx) => (
                  <Box key={itemIdx} component="li">
                    <Typography variant="body2" component="span" sx={{ lineHeight: 1.65 }}>
                      {renderInlineMarkdown(item)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          case 'blockquote':
            return (
              <Box
                key={`bq-${index}`}
                sx={{
                  borderLeft: 3,
                  borderColor: 'divider',
                  pl: 1.5,
                  py: 0.5,
                  mb: 1.25,
                  color: 'text.secondary',
                }}
              >
                {block.lines.map((line, lineIdx) => (
                  <Typography key={lineIdx} variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.65 }}>
                    {renderInlineMarkdown(line)}
                  </Typography>
                ))}
              </Box>
            );
          case 'hr':
            return <Divider key={`hr-${index}`} sx={{ my: 1.5 }} />;
          case 'code':
            return (
              <Box
                key={`code-${index}`}
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.82rem',
                  p: 1.5,
                  my: 1.25,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {block.content}
              </Box>
            );
          default:
            return null;
        }
      })}
    </Box>
  );
};
